import { FC, useState } from 'react'
import { useGetList, useTranslate } from 'react-admin'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import SearchIcon from '@mui/icons-material/Search'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { ParameterMapping, ToolCallNodeData, WorkflowNodeData } from '../types'
import { themeColor, xxllncColor } from '../../../layout/theme/color'
import { McpServerOut, McpToolOut } from '../../../types/apiRequests'
import TestToolCallDialog from '../components/TestToolCallDialog'
import DisclosureSection from '../components/DisclosureSection'
import ParameterValueField from './ParameterValueField'
import { filterParamsForTool } from './parameterUtils'

interface ToolCallConfigProps {
  data: ToolCallNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
  nodeId: string
  workflowId?: string
  testConfig?: Record<string, unknown>
}

interface ToolParam {
  name: string
  type: string
  description: string
  required: boolean
}

const toToolParam = (param: Record<string, unknown>): ToolParam => ({
  name: param.name as string,
  type: param.type as string,
  description: (param.description as string) ?? '',
  required: (param.required as boolean) ?? false,
})

const isFilled = (mapping?: ParameterMapping): boolean =>
  mapping !== undefined && mapping.value !== '' && mapping.value !== undefined

const optionLabelSx = { fontWeight: 500 } as const

const ToolCallConfig: FC<ToolCallConfigProps> = ({ data, onUpdate, nodeId, workflowId, testConfig }) => {
  const translate = useTranslate()
  const [testOpen, setTestOpen] = useState(false)
  const [removedNotice, setRemovedNotice] = useState(0)

  const { data: servers, isLoading: serversLoading } = useGetList<McpServerOut>('mcp_server', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'display_name', order: 'ASC' },
    filter: { enabled: true },
  })

  const { data: tools, isLoading: toolsLoading } = useGetList<McpToolOut>('mcp_tool', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    // eslint-disable-next-line camelcase
    filter: data.serverId ? { server_id: data.serverId } : {},
  })

  const serverTools = tools?.filter((t) => t.server_id === data.serverId) ?? []

  const selectedServer = servers?.find((s) => s.id === data.serverId) ?? null
  const selectedTool =
    serverTools.find((t) => t.id === data.toolId) ??
    serverTools.find((t) => t.name === data.toolName)

  // A saved node referencing a since-disabled tool keeps it in the list so
  // its configuration stays visible (with a warning) instead of blanking out.
  const selectableTools = serverTools.filter(
    (t) => t.enabled || t.id === selectedTool?.id,
  )
  const selectedToolDisabled = selectedTool !== undefined && !selectedTool.enabled

  const handleServerChange = (serverId: string) => {
    const server = servers?.find((s) => s.id === serverId)
    setRemovedNotice(0)
    onUpdate({
      serverId,
      serverName: server?.name ?? '',
      toolId: '',
      toolName: '',
      parameters: {},
      lastOutputFields: [],
    })
  }

  const handleToolChange = (toolId: string) => {
    const tool = serverTools.find((t) => t.id === toolId)
    // Drop mappings the new tool doesn't declare — but only when the tool
    // definition is actually loaded, so a slow fetch can't wipe everything.
    let parameters = data.parameters
    let removed = 0
    if (tool && Array.isArray(tool.parameters)) {
      const declared = new Set(tool.parameters.map((p: Record<string, unknown>) => p.name as string))
      const result = filterParamsForTool(data.parameters ?? {}, declared)
      parameters = result.kept
      removed = result.removedCount
    }
    setRemovedNotice(removed)
    onUpdate({
      toolId,
      toolName: tool?.name ?? '',
      label: tool?.name ?? data.label,
      parameters,
      lastOutputFields: [],
    })
  }

  const handleTestSuccess = (fields: string[]) => {
    // Merge additively: a partial test response must not erase fields seen
    // in an earlier, richer test.
    const merged = Array.from(new Set([...(data.lastOutputFields ?? []), ...fields])).sort()
    onUpdate({ lastOutputFields: merged })
  }

  const declaredParams = (selectedTool?.parameters ?? []).map(toToolParam)
  const requiredParams = declaredParams.filter((p) => p.required)
  const optionalParams = declaredParams.filter((p) => !p.required)
  const filledOptionalCount = optionalParams.filter((p) =>
    isFilled(data.parameters?.[p.name]),
  ).length

  // Mappings outside the tool's declared schema still execute (the executor
  // sends every mapping), so surface them for inspection or removal.
  const declaredNames = new Set(declaredParams.map((p) => p.name))
  const extraNames = Object.keys(data.parameters ?? {}).filter((n) => !declaredNames.has(n))

  const removeParameter = (name: string) => {
    const next = { ...data.parameters }
    // eslint-disable-next-line security/detect-object-injection
    delete next[name]
    onUpdate({ parameters: next })
  }

  const renderParamField = (param: ToolParam) => (
    <ParameterValueField
      key={`${nodeId}-${data.toolId}-${param.name}`}
      label={param.name}
      paramType={param.type}
      paramDesc={param.description}
      paramRequired={param.required}
      mapping={data.parameters[param.name]}
      onChange={(mapping) =>
        onUpdate({
          parameters: {
            ...data.parameters,
            [param.name]: mapping,
          },
        })
      }
    />
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      {serversLoading ? (
        <CircularProgress size={20} />
      ) : (
        <Autocomplete<McpServerOut, false, false, false>
          fullWidth
          size="small"
          options={servers ?? []}
          value={selectedServer}
          onChange={(_e, server) => handleServerChange(server?.id ?? '')}
          getOptionLabel={(server) => server.display_name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.toLowerCase().trim()
            if (!q) return options
            return options.filter(
              (s) =>
                s.display_name.toLowerCase().includes(q) ||
                s.name.toLowerCase().includes(q),
            )
          }}
          renderOption={(props, server) => (
            <li {...props} key={server.id}>
              <Box>
                <Typography variant="body2" sx={optionLabelSx}>
                  {server.display_name}
                </Typography>
                <Typography variant="caption" sx={{ color: themeColor.slate50 }}>
                  {server.name}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate('workflow.config.server')}
              placeholder={translate('workflow.sidebar.search')}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: themeColor.slate40 }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      )}

      {data.serverId && (
        <Autocomplete<McpToolOut, false, false, false>
          fullWidth
          size="small"
          options={selectableTools}
          loading={toolsLoading}
          value={selectedTool ?? null}
          onChange={(_e, tool) => handleToolChange(tool?.id ?? '')}
          getOptionLabel={(tool) => tool.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.toLowerCase().trim()
            if (!q) return options
            return options.filter(
              (t) =>
                t.name.toLowerCase().includes(q) ||
                t.description.toLowerCase().includes(q),
            )
          }}
          renderOption={(props, tool) => (
            <li {...props} key={tool.id}>
              <Box>
                <Typography variant="body2" sx={optionLabelSx}>
                  {tool.name}
                  {!tool.enabled && ` — ${translate('workflow.config.toolDisabledSuffix')}`}
                </Typography>
                <Typography variant="caption" sx={{ color: themeColor.slate50 }}>
                  {tool.description}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate('workflow.config.tool')}
              placeholder={translate('workflow.sidebar.search')}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: themeColor.slate40 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {toolsLoading && (
                      <CircularProgress
                        size={16}
                        sx={{ mr: 1, color: themeColor.slate40 }}
                      />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {!data.serverId && (
        <Typography
          variant="caption"
          sx={{ color: themeColor.slate50, lineHeight: 1.4 }}
        >
          {translate('workflow.config.toolEmptyState')}
        </Typography>
      )}

      {selectedToolDisabled && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ 'py': 0, 'fontSize': 12, '& .MuiAlert-icon': { fontSize: 18, py: 0.75 } }}
        >
          {translate('workflow.config.toolDisabledWarning')}
        </Alert>
      )}

      {removedNotice > 0 && (
        <Alert
          severity="warning"
          variant="outlined"
          onClose={() => setRemovedNotice(0)}
          sx={{ 'py': 0, 'fontSize': 12, '& .MuiAlert-icon': { fontSize: 18, py: 0.75 } }}
        >
          {/* eslint-disable-next-line camelcase */}
          {translate('workflow.config.paramsRemoved', { smart_count: removedNotice })}
        </Alert>
      )}

      {selectedTool && (declaredParams.length > 0 || extraNames.length > 0) && (
        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: themeColor.slate60, mb: 1, display: 'block' }}
          >
            {translate('workflow.config.parameters')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {requiredParams.map(renderParamField)}
          </Box>

          {optionalParams.length > 0 && (
            <Box sx={{ mt: requiredParams.length > 0 ? 1.5 : 0 }}>
              <DisclosureSection
                title={translate('workflow.config.optionalFields', {
                  // eslint-disable-next-line camelcase
                  smart_count: optionalParams.length,
                })}
                defaultOpen={false}
                forceOpen={filledOptionalCount > 0}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {optionalParams.map(renderParamField)}
                </Box>
              </DisclosureSection>
            </Box>
          )}

          {extraNames.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: xxllncColor.orange100, mb: 0.5, display: 'block' }}
              >
                {translate('workflow.config.paramsNotInSchema')}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: themeColor.slate50, mb: 1, display: 'block', lineHeight: 1.3 }}
              >
                {translate('workflow.config.paramsNotInSchemaHint')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {extraNames.map((paramName) => (
                  <Box key={`${nodeId}-extra-${paramName}`} sx={{ display: 'flex', gap: 0.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <ParameterValueField
                        label={paramName}
                        paramType={translate('workflow.config.paramNotInSchemaType')}
                        paramDesc=""
                        paramRequired={false}
                        // eslint-disable-next-line security/detect-object-injection
                        mapping={data.parameters[paramName]}
                        onChange={(mapping) =>
                          onUpdate({
                            parameters: {
                              ...data.parameters,
                              [paramName]: mapping,
                            },
                          })
                        }
                      />
                    </Box>
                    <Tooltip title={translate('workflow.config.paramRemove')}>
                      <IconButton
                        size="small"
                        onClick={() => removeParameter(paramName)}
                        sx={{ mt: 0.5, color: themeColor.slate40 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {selectedTool && (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={() => setTestOpen(true)}
            sx={{ textTransform: 'none', mt: 1 }}
          >
            {translate('workflow.config.testCall')}
          </Button>
          <TestToolCallDialog
            open={testOpen}
            onClose={() => setTestOpen(false)}
            nodeData={data}
            nodeId={nodeId}
            workflowId={workflowId}
            testConfig={testConfig}
            onTestSuccess={handleTestSuccess}
            paramInfo={Object.fromEntries(
              declaredParams.map((p) => [
                p.name,
                { type: p.type, description: p.description, required: p.required },
              ]),
            )}
          />
        </>
      )}
    </Box>
  )
}

export default ToolCallConfig
